<template>
  <div class="page-content">
    <div class="headings">
      <h2 class="title title-primary">{{ location }}</h2>
      <span>|</span>
      <h2 class="title title-primary">
        {{ $t("lang.pos-exit.dashboard.page_title_line_1") }}
      </h2>
      <span>|</span>
      <h3 class="title title-primary">
        {{ $t("lang.pos-exit.dashboard.page_title_line_2") }}
      </h3>
    </div>
    <div class="page-header">
      <div class="boxes" v-if="!shouldLoading">
        <bx-box
          dark
          color="outline"
          class="col-12 col-sm-auto p-1 bx-box-adjustable"
        >
          <bx-value
            :label="$t('lang.pos-exit.dashboard.box_title_1')"
            :value="ordersCount"
            font-size="xs"
          />
        </bx-box>
      </div>

      <skeleton-boxes progress-box :boxes="3" v-else />
    </div>

    <div class="page-main-content">
      <div class="container-fluid">
        <div class="row">
          <bx-box
            :title="
              ordersCount + ' ' + $t('lang.pos-exit.dashboard.table_title')
            "
            color="white"
            no-padding
            class="col-12"
            :loading="shouldLoading"
          >
            <template v-slot:default>
              <bx-table
                :data="data"
                :columns="columns"
                paginated
                backendPagination
                :currentPage="page"
                :perPage="itemsPerPage"
                :total="ordersCount"
                v-if="!shouldLoading"
                @search="onSearch"
                @update:currentPage="updatePage"
              >
                <template slot-scope="props">
                  <bx-table-column centered>
                    <router-link
                      :to="`/pos/exits/${props.row.id}`"
                      class="bx-link bx-link-bold"
                      >{{ props.row.id }}</router-link
                    >
                  </bx-table-column>
                  <bx-table-column centered>{{
                    formatDate(props.row.end_time)
                  }}</bx-table-column>
                  <bx-table-column centered>{{
                    props.row.item_count
                  }}</bx-table-column>
                </template>
              </bx-table>
            </template>
          </bx-box>
        </div>
        <v-row class="d-flex d-sm-none">
          <v-list width="100%">
            <div
              v-infinite-scroll="loadMore"
              infinite-scroll-disabled="busy"
              infinite-scroll-distance="20"
              style="width: 100%"
            >
              <v-list-item
                two-line
                v-for="(item, index) in dataList"
                :key="index"
                class="green lighten-5 ma-2 rounded-lg"
                ripple
                :to="`/pos/exits/${item.id}`"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    <div>
                      {{ item.id }}
                    </div>
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-list-item-subtitle class="text-caption">
                    {{ item.item_count }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle class="text-caption">
                    {{ formatDate(item.end_time) }}
                  </v-list-item-subtitle>
                </v-list-item-action>
              </v-list-item>
            </div>
          </v-list>
        </v-row>
        <skeleton-table :columns="3" v-if="shouldLoading" />
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import BxTableColumn from "../../../bx-components/BxTableColumn";
import common from "@/utils/common";

export default {
  name: "pos-exit-dashboard",
  components: { BxTableColumn },
  mixins: [common],
  data() {
    return {
      key: "movement",
      shouldLoading: true,
      columns: [],
      data: [],
      dataList: [],
      movStats: {},
      ordersCount: 0,
      API_BASE: process.env.VUE_APP_API_BASE,
      reloadInterval: null,
      location: null,
      locationId: null,
      itemsPerPage: process.env.VUE_APP_ITEMS_PER_PAGE,
      page: 1,
      filters: {
        id: null,
        end_time: null
      }
    };
  },
  methods: {
    loadMore() {
      if (
        !this.shouldLoading &&
        this.dataList.length &&
        this.dataList.length < this.ordersCount &&
        this.isMobile
      ) {
        this.page++;
        this.getOrders(this.locationId);
      }
    },
    formatDate(date) {
      return date
        ? moment(date).format("DD/ MM/ YYYY, h:mm a")
        : this.$t("lang.messages.no_date_available");
    },
    onSearch(field, value) {
      this.$session.set(this.key + field, value);
      this.filters[field] = value;
      this.getData();
    },
    updatePage(p) {
      this.page = p;
      this.getOrders(this.locationId);
    },
    async getOrders(locationId) {
      try {
        if (this.isMobile) {
          this.shouldLoading = true;
        }
        // Load orders
        const orders = await this.$api.location.sales(locationId, {
          offset: (this.page - 1) * this.itemsPerPage,
          limit: this.itemsPerPage,
          id__contains: this.filters.id,
          end_time__gte: this.filters.end_time && this.filters.end_time.date_start,
          end_time__lte: this.filters.end_time && this.filters.end_time.date_end
        });
        this.ordersCount = orders.count;
        this.data = orders.results.map(item => {
          let isAfter = moment().isAfter(item.estimated_arrival_date);

          return {
            id: item.id,
            origin_status: item.origin_status,
            destination_status: item.destination_status,
            waiting_time: item.destination_waiting_time,
            item_count: item.item_count,
            real_item_count: item.destination_real_item_count,
            num_boxes: item.num_boxes,
            num_verified_boxes: item.destination_num_verified_boxes,
            creation_date: item.creation_date,
            end_time: item.end_time,
            destination: item.destination,
            time_color: isAfter ? "bx-text-red" : "bx-text-black"
          };
        });

        if (this.isMobile) {
          this.dataList = this.dataList.concat(this.data);
        }

        this.columns = [
          {
            field: "id",
            label: this.$t("lang.pos-exit.dashboard.table_field_3"),
            filter: {
              placeholder: this.$t(
                "lang.pos-exit.dashboard.table_field_placeholder_3"
              ),
              value: this.filters.id
            }
          },
          {
            field: "end_time",
            label: this.$t("lang.pos-exit.dashboard.table_field_4"),
            filter: {
              placeholder: this.$t(
                "lang.pos-exit.dashboard.table_field_placeholder_4"
              ),
              value: this.filters.end_time,
              isdateRange: true
            },
            centered: true
          },
          {
            field: "item_count",
            label: this.$t("lang.pos-exit.dashboard.table_field_6"),
            centered: true
          }
        ];
      } catch (error) {
        console.log(error)
      } finally {
        this.shouldLoading = false;
      }
    },
    async getData() {
      try {
        // Load stats
        this.location = localStorage.getItem("location");
        this.locationId = localStorage.getItem("location_id");

        if (!location) {
          try {
            let firstLocation = await this.$api.location.search("", {
              limit: 1,
              offset: 0,
              orderBy: "id"
            });
            location = firstLocation.results[0].description;
            this.locationId = firstLocation.results[0].id;
          } catch (error) {
            console.log(error);
          }
        }

        // Load orders
        await this.getOrders(this.locationId);
      } catch (error) {
        // console.log(error)
      }
    }
  },
  mounted() {
    if (!this.isMobile) {
      this.reloadInterval = setInterval(
        this.getData,
        process.env.VUE_APP_RELOAD_TIME
      );
    }
    this.filters.id = this.$session.get(this.key + "id");
    this.filters.end_time = this.$session.get(this.key + "end_time");
    this.getData();
  },
  beforeDestroy() {
    clearInterval(this.reloadInterval);
  }
};
</script>

<style scoped>
@media screen and (max-width: 480px) {
  .bx-box-adjustable >>> .bx-box-content {
    min-height: 109px;
  }

  .bx-box-adjustable >>> .bx-value-value {
    margin-top: 15px;
  }
}
</style>
